import { ColumnDefResolved, Row } from '@tanstack/react-table';
import {
  AggregatedMetricsTableData,
  MetricsTableData,
  showResultAsPercentage,
} from '../../DisclosureRequirements';
import { NestedTable, TableData } from 'Molecules/NestedTable';
import { useMemo } from 'react';
import { HStack } from '@chakra-ui/react';
import { IconButton, TruncatableText } from 'Atoms';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { MetricTypeIcon, MetricTypes } from 'Molecules/MetricTypeIcon';
import { mapUnitToCompanyCurrency } from 'containers/Esrs/utils';
import { Typography } from 'Tokens';
import { MetricRowTitle } from '../../DisclosureRequirements/Metrics/MetricsTable/MetricsTableComponents/MetricRowTitle/MetricRowTitle';
import { getTableRowText } from '../DocGenerator/report-helper-functions';

const ReportMetricTableTitle = ({
  row,
  currency,
}: {
  row: Row<TableData<AggregatedMetricsTableData>>;
  currency: string;
}) => {
  const unit = mapUnitToCompanyCurrency(row.original.metric.unitOfMeasurement ?? 'NA', currency);

  return (
    <HStack pl={`${row.depth * 24}px`} spacing="8px">
      {row.getCanExpand() ? (
        <IconButton
          variant={'ghost'}
          size="xs"
          onClick={(e) => {
            e.stopPropagation();
            row.getToggleExpandedHandler()();
          }}
          aria-label="expand"
          icon={row.getIsExpanded() ? <ChevronDownIcon /> : <ChevronRightIcon />}
        />
      ) : (
        <MetricTypeIcon type={MetricTypes.number} />
      )}
      <MetricRowTitle row={row} isReport={true} unit={unit} />
    </HStack>
  );
};

const ReportMetricTableValue = ({ row }: { row: Row<TableData<AggregatedMetricsTableData>> }) => {
  const value = getTableRowText(row.original);

  const isUmbrellaMetric = row.original.metric.calculation === 'NONE';

  const parentRow = row.getParentRow() ?? row;

  return (
    <HStack>
      <TruncatableText variant="body" text={isUmbrellaMetric ? '' : `${value}`} />

      {row.original.metric.showAsPercentage && (
        <Typography variant="body">
          {showResultAsPercentage(row.original.result?.Year, parentRow.original.result?.Year)}
        </Typography>
      )}
    </HStack>
  );
};

export const ReportMetricTable = ({
  metrics,
  currency,
  standardId,
}: {
  metrics: AggregatedMetricsTableData[];
  currency: string;
  standardId: string;
}) => {
  const isUmbrellaMetric = metrics[0].metric.calculation === 'NONE';
  const nestedColumns: ColumnDefResolved<TableData<AggregatedMetricsTableData>, any>[] | undefined =
    useMemo(
      () => [
        {
          header: isUmbrellaMetric ? metrics[0].metric.title : 'Metric',
          accessorKey: 'metric',
          meta: {
            width: '800px',
          },
          cell: ({ row }) => <ReportMetricTableTitle row={row} currency={currency} />,
        },
        {
          header: 'Total',
          meta: {
            width: '150px',
          },
          accessorKey: 'yearly',
          cell: ({ row }) => <ReportMetricTableValue row={row} />,
        },
      ],
      [metrics, currency, standardId]
    );

  const processedData = useMemo(() => {
    // Remove the top row for umbrella metrics
    if (isUmbrellaMetric && metrics[0].subRows) {
      return metrics[0].subRows;
    }
    return metrics;
  }, [metrics]);

  return (
    <NestedTable<MetricsTableData>
      columns={nestedColumns}
      data={processedData}
      expanded={true}
      withOuterBorder={true}
      borderRadius="0px"
      rowHeight="36px"
      defaultExpanded={true}
    />
  );
};
